import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  Check, Loader2, Sparkles, MessageSquare, X, AlertTriangle, Gift, Heart, CheckCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import { TIERS } from '../../constants/tiers';
import { annualSavingsMonths } from '../../constants/annualSavings';
import { notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const SUCCESS_PATH = '/portal/dashboard?upgraded=true';
const CANCEL_PATH = '/portal/subscription';

// ============================================================
// Per-tier feature bullets - mirrors the wording on /portal/upgrade so
// members see consistent value props across both panels.
// ============================================================
const FEATURES = {
  blueprint_only: [
    'Personalized Blueprint generation',
    'Comprehensive Blueprint PDF roadmap (40+ pages)',
    'Interactive Mexico relocation checklist',
    'AI support chat - ask anything during your move',
  ],
  essentials: [
    'Everything in Blueprint Only',
    'Full Classroom library (8 modules)',
    'Full THE MOVE access - checklist, boards, partners',
    '1 × 45-minute strategy call',
  ],
  guided: [
    'Everything in Essentials',
    '3 × 45-min 1:1 coaching calls with a relocation specialist',
    '3 free Mundalo City Guides - auto-selected from your intake',
    '1 full year of Community membership - included',
  ],
  community: [
    'Everything in THE MOVE',
    'Community Board · Events · Housing · Roommates',
    '15+ Mexico expat Local Deals (updated weekly)',
    'Cancel anytime',
  ],
  connected: [
    'Everything in Community',
    'Monthly Expert Q&A sessions',
    'Priority housing notifications',
  ],
};

// ============================================================
// Plan card - cream-on-cream, Mundalo brand
// ============================================================
const TierCard = ({ tier, onChoose, loading, currentRole }) => {
  const isCurrent = currentRole === tier.id;
  const features = FEATURES[tier.id] || [];
  return (
    <article
      className={`bg-white border rounded-xl p-6 flex flex-col relative ${
        tier.primary ? 'border-[#E8A830] shadow-lg' : 'border-[#EFE6CF]'
      }`}
      data-testid={`subscription-tier-${tier.id}`}
    >
      {tier.badge && (
        <span
          className="absolute -top-3 left-6 bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-[9px] tracking-[0.18em] font-bold px-3 py-1 rounded-full"
          data-testid={`subscription-tier-${tier.id}-badge`}
        >
          {tier.badge}
        </span>
      )}
      {isCurrent && (
        <span
          className="absolute -top-3 right-6 bg-[#2CA8A0] text-white font-mono uppercase text-[9px] tracking-[0.18em] font-bold px-3 py-1 rounded-full"
          data-testid={`subscription-tier-${tier.id}-current-badge`}
        >
          Current
        </span>
      )}

      <header className="mb-4">
        <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-1">{tier.name}</h3>
        <div className="flex items-baseline gap-1.5 mb-1">
          <span className="font-display italic text-4xl text-[#0A1F2E]">
            ${tier.price}
          </span>
          <span className="font-mono uppercase text-[11px] tracking-[0.12em] text-[#0A1F2E]/55">
            / {tier.interval}
          </span>
        </div>
        {tier.priceAnnual && (
          <p className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#1A5C6E]">
            or ${tier.priceAnnual}/yr - {annualSavingsMonths(tier.id)} months free
          </p>
        )}
        <p className="font-body text-sm text-[#0A1F2E]/70 mt-3 leading-snug">{tier.description}</p>
      </header>

      <ul className="space-y-2 mb-6 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-[#0A1F2E]/80">
            <Check className="w-4 h-4 text-[#2CA8A0] flex-shrink-0 mt-0.5" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {isCurrent ? (
        <button
          disabled
          className="w-full bg-[#F4ECD8] text-[#0A1F2E]/60 font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full cursor-default"
          data-testid={`subscription-tier-${tier.id}-current`}
        >
          Current plan
        </button>
      ) : tier.comingSoon ? (
        <button
          disabled
          className="w-full bg-[#F4ECD8] text-[#0A1F2E]/70 font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full cursor-not-allowed border border-[#EFE6CF]"
          data-testid={`subscription-tier-${tier.id}-coming-soon`}
        >
          Coming Soon
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onChoose(tier.id)}
          disabled={loading === tier.id}
          className={`w-full font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full transition disabled:opacity-50 ${
            tier.primary
              ? 'bg-[#E8A830] text-[#0A1F2E] hover:bg-[#d59824]'
              : 'bg-[#0A1F2E] text-white hover:bg-[#1A5C6E]'
          }`}
          data-testid={`subscription-tier-${tier.id}-cta`}
        >
          {loading === tier.id ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : tier.cta}
        </button>
      )}
    </article>
  );
};

// ============================================================
// Cancellation modal - restyled to brand (cream sheet, teal accents).
// Keeps the three-step retention flow intact.
// ============================================================
const CancellationModal = ({ isOpen, onClose, onConfirmCancel, onOfferApplied }) => {
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [offerSuccess, setOfferSuccess] = useState(null);

  if (!isOpen) return null;

  const applyOffer = async (offerKey, success) => {
    setProcessing(true);
    try {
      const r = await fetch(`${API_URL}/api/subscription/apply-retention-offer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ offer: offerKey }),
      });
      if (r.ok) {
        setOfferSuccess(success);
        onOfferApplied && onOfferApplied(offerKey);
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not apply offer.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleFinalCancel = async () => {
    setProcessing(true);
    await onConfirmCancel();
    setProcessing(false);
  };

  const handleClose = () => {
    setStep(1);
    setOfferSuccess(null);
    onClose();
  };

  const Card = ({ children, testId }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid={testId}>
      <div className="absolute inset-0 bg-[#0A1F2E]/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white border border-[#EFE6CF] rounded-2xl max-w-md w-full p-6 sm:p-8 shadow-xl">
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#0A1F2E]/40 hover:text-[#0A1F2E] transition"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
        {children}
      </div>
    </div>
  );

  if (offerSuccess) {
    return (
      <Card testId="offer-success-modal">
        <div className="text-center">
          <CheckCircle className="w-14 h-14 text-[#2CA8A0] mx-auto mb-4" />
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-2" data-testid="offer-success-title">
            {offerSuccess.message}
          </h2>
          <p className="text-[#0A1F2E]/70 text-sm mb-6">{offerSuccess.detail}</p>
          <button
            onClick={handleClose}
            className="w-full bg-[#0A1F2E] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full hover:bg-[#1A5C6E] transition"
            data-testid="offer-success-close"
          >
            Continue to Portal
          </button>
          <p className="text-[#0A1F2E]/45 text-xs mt-4">Thank you for staying with us.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card testId="cancel-modal">
      {step === 1 && (
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-[#E8A830] mx-auto mb-4" />
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-3">
            Are you sure you want to go it alone?
          </h2>
          <p className="text-[#0A1F2E]/70 text-sm mb-6 leading-relaxed">
            We've been guiding you every step of your relocation journey. Handling visas, banking,
            housing, and all the paperwork on your own can be overwhelming.{' '}
            <span className="text-[#2CA8A0] font-medium">Let's keep going together.</span>
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleClose}
              className="w-full bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full hover:bg-[#d59824] transition"
            >
              You're right, I'll stay
            </button>
            <button
              onClick={() => setStep(2)}
              className="w-full text-[#0A1F2E]/55 hover:text-[#0A1F2E] py-2 text-sm transition"
            >
              I still want to cancel
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="text-center">
          <Gift className="w-12 h-12 text-[#2CA8A0] mx-auto mb-4" />
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-3">Stay and save 50%</h2>
          <p className="text-[#0A1F2E]/70 text-sm mb-4">
            We don't want to see you go. Here's a special offer just for you:
          </p>
          <div className="bg-[#F4ECD8] border border-[#EFE6CF] p-4 rounded-xl mb-6">
            <p className="font-display italic text-3xl text-[#0A1F2E] mb-1">
              $14.50<span className="text-sm font-mono uppercase tracking-[0.12em] text-[#0A1F2E]/55"> /mo</span>
            </p>
            <p className="text-[#0A1F2E]/55 text-xs">50% off for the next 3 months</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => applyOffer('50_off_3_months', {
                message: 'Your 50% discount has been applied!',
                detail: 'Your next 3 bills will be $14.50/month instead of $29/month.',
              })}
              disabled={processing}
              className="w-full bg-[#2CA8A0] text-white font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full hover:bg-[#1A8E86] transition disabled:opacity-50"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Yes, give me 50% off'}
            </button>
            <button
              onClick={() => setStep(3)}
              className="w-full text-[#0A1F2E]/55 hover:text-[#0A1F2E] py-2 text-sm transition"
            >
              No thanks, continue cancelling
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center">
          <Heart className="w-12 h-12 text-[#E8A830] mx-auto mb-4" />
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-3">Last chance: one month on us</h2>
          <p className="text-[#0A1F2E]/70 text-sm mb-4">
            We really value having you. Your next month is completely free if you stay.
          </p>
          <div className="bg-[#F4ECD8] border border-[#EFE6CF] p-4 rounded-xl mb-6">
            <p className="font-display italic text-3xl text-[#0A1F2E] mb-1">$0</p>
            <p className="text-[#0A1F2E]/55 text-xs">Next month completely free</p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => applyOffer('1_month_free', {
                message: 'Your free month has been applied!',
                detail: 'Your next bill will be $0. After that, regular pricing resumes.',
              })}
              disabled={processing}
              className="w-full bg-[#E8A830] text-[#0A1F2E] font-mono uppercase text-[11px] tracking-[0.15em] py-3 rounded-full hover:bg-[#d59824] transition disabled:opacity-50"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Give me the free month'}
            </button>
            <button
              onClick={handleFinalCancel}
              disabled={processing}
              className="w-full text-[#B91C1C] hover:text-[#7F1414] py-2 text-sm transition"
            >
              {processing ? 'Cancelling…' : 'Cancel my subscription'}
            </button>
          </div>
          <p className="text-[#0A1F2E]/45 text-xs mt-4">
            Your access will continue until the end of your current billing period.
          </p>
        </div>
      )}
    </Card>
  );
};

// ============================================================
// Page
// ============================================================
const PortalSubscription = () => {
  const { user, loading } = useAuth();
  const [userTier, setUserTier] = useState(user?.role || 'free_trial');
  const [portalProfile, setPortalProfile] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [retentionApplied, setRetentionApplied] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/portal/me`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setPortalProfile(data.profile);
          if (data.profile?.tier) setUserTier(data.profile.tier);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      }
    })();
  }, [user]);

  const startCheckout = async (planId) => {
    setCheckoutLoading(planId);
    try {
      const r = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: planId,
          billing_period: planId === 'community' || planId === 'connected' ? 'monthly' : 'onetime',
          success_path: SUCCESS_PATH,
          cancel_path: CANCEL_PATH,
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.url) {
        window.location.href = d.url;
      } else {
        notifyError(d, 'Could not start checkout. Please try again.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleConfirmCancel = async () => {
    try {
      const r = await fetch(`${API_URL}/api/subscription/cancel`, {
        method: 'POST',
        credentials: 'include',
      });
      if (r.ok) {
        setCancelSuccess(true);
        setShowCancelModal(false);
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not cancel - please contact support.');
      }
    } catch {
      notifyError('Network error. Please try again.');
    }
  };

  if (loading) {
    return (
      <PortalLayout activeTab="subscription">
        <div className="py-20 text-center text-[#0A1F2E]/55">Loading…</div>
      </PortalLayout>
    );
  }
  if (!user) return <Navigate to="/portal" replace />;

  // Recurring subscription tiers that can be "cancelled" via the retention flow.
  // iter 182.g - Two separate role signals: `userTier` reflects the
  // relocation-profile tier (coordinated/guided/etc. from /portal/me)
  // used to pick the correct UPGRADE cards. The subscription/cancel
  // panels below key off `user.role` directly because that's what maps
  // 1:1 to whether the account carries a recurring Stripe subscription.
  const authRole = (user?.role || 'free_trial').toLowerCase();
  const isRecurring = ['community', 'connected', 'bundle_monthly', 'bundle_annual', 'free_trial'].includes(authRole);

  const oneTimeTiers = [TIERS.blueprint_only, TIERS.essentials, TIERS.guided];
  const recurringTiers = [TIERS.community, TIERS.connected];

  return (
    <PortalLayout activeTab="subscription">
      <SEO title="Subscriptions - Mundalo" description="Manage your Mundalo plan." path="/portal/subscription" />

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="portal-subscription-page">
        <header className="mb-8">
          <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#E8A830] mb-2 inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Your plan
          </p>
          <h1 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-[1.05] mb-2">
            Subscriptions
          </h1>
          <p className="font-body text-base text-[#0A1F2E]/70">
            View your current plan and explore upgrade options.
          </p>
        </header>

        {retentionApplied && (
          <div
            className="mb-6 p-4 bg-[#2CA8A0]/10 border border-[#2CA8A0]/40 rounded-xl text-[#0A1F2E] text-sm flex items-start gap-2"
            data-testid="retention-applied-message"
          >
            <CheckCircle className="w-5 h-5 text-[#2CA8A0] flex-shrink-0 mt-0.5" />
            <span>
              {retentionApplied === '50_off_3_months'
                ? 'Your 50% discount is active. Next 3 bills will be $14.50/month.'
                : 'Your free month is active. Next bill will be $0.'}
            </span>
          </div>
        )}

        {cancelSuccess && (
          <div className="mb-6 p-4 bg-[#E8A830]/10 border border-[#E8A830]/40 rounded-xl text-[#0A1F2E] text-sm">
            Your subscription has been cancelled. You'll have access until the end of your current billing period.
          </div>
        )}

        <section className="mb-12">
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-1">THE MOVE - One-time</h2>
          <p className="font-body text-sm text-[#0A1F2E]/65 mb-6">
            Pay once. The fastest, lowest-risk way to relocate.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {oneTimeTiers.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                onChoose={startCheckout}
                loading={checkoutLoading}
                currentRole={userTier}
              />
            ))}
          </div>
        </section>

        <section className="mb-12">
          <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-1">THE LIFE - Monthly or Annual</h2>
          <p className="font-body text-sm text-[#0A1F2E]/65 mb-6">
            Already moved? Stay connected to the Mexico expat community + benefits.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-4xl">
            {recurringTiers.map((t) => (
              <TierCard
                key={t.id}
                tier={t}
                onChoose={startCheckout}
                loading={checkoutLoading}
                currentRole={userTier}
              />
            ))}
          </div>
        </section>

        {isRecurring && !cancelSuccess && (
          <section
            className="mt-12 p-6 bg-white border border-[#EFE6CF] rounded-xl"
            data-testid="cancel-subscription-section"
          >
            <h3 className="font-display italic text-xl text-[#0A1F2E] mb-2">Manage subscription</h3>
            <p className="text-[#0A1F2E]/65 text-sm mb-4">
              Cancel, pause, or update your card in the secure Stripe portal.
              Your access continues until the end of your current billing period.
            </p>
            <div className="flex flex-wrap gap-3">
              {/* iter 182.g - self-serve Stripe Customer Portal link */}
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/subscription/portal`,
                      {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ return_url: window.location.href }),
                      },
                    );
                    const d = await res.json();
                    if (d.portal_url) window.location.href = d.portal_url;
                  } catch { /* noop */ }
                }}
                className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white text-sm font-medium px-5 py-2.5 rounded-full transition"
                data-testid="stripe-portal-btn"
              >
                Open Stripe portal
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="text-[#B91C1C] hover:text-[#7F1414] text-sm font-medium transition self-center"
                data-testid="cancel-subscription-btn"
              >
                Cancel my subscription
              </button>
            </div>
          </section>
        )}

        <section
          className="mt-8 bg-[#F4ECD8] border border-[#EFE6CF] rounded-xl p-6 md:p-8 text-center max-w-3xl mx-auto"
          data-testid="subscription-help"
        >
          <h3 className="font-display italic text-xl text-[#0A1F2E] mb-2">Need help choosing?</h3>
          <p className="text-[#0A1F2E]/70 text-sm mb-4">
            Most members start with <strong className="text-[#0A1F2E]">Guided</strong> for the move,
            then add <strong className="text-[#0A1F2E]">Connected</strong> once they're on the ground.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/portal/blueprint"
              className="inline-flex items-center gap-2 text-[#2CA8A0] hover:text-[#1A8E86] transition text-sm font-medium"
            >
              <MessageSquare className="w-4 h-4" /> Ask the Assistant
            </Link>
            <span className="hidden sm:inline text-[#0A1F2E]/30">·</span>
            <a
              href="mailto:hello@mundalo.com"
              className="text-[#0A1F2E]/65 hover:text-[#0A1F2E] transition text-sm"
            >
              Contact hello@mundalo.com
            </a>
          </div>
        </section>
      </div>

      <CancellationModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirmCancel={handleConfirmCancel}
        onOfferApplied={(offer) => setRetentionApplied(offer)}
      />
    </PortalLayout>
  );
};

export default PortalSubscription;
